import { Injectable } from '@angular/core';
import { BehaviorSubject, debounceTime, skip } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { AppTheme, ThemeService } from 'src/app/core/_services/theme.service';

/** localStorage ključ podešavanja uređaja. ⚠ SYNC: pre-boot skripta u index.html čita temu odavde. */
const STORAGE_KEY = 'settings';

/**
 * Podešavanja uređaja delivery app-a (čuvaju se lokalno, u localStorage "settings").
 * Za sada samo tema — ostala podešavanja vozača su u native shell-u.
 */
export class Settings {
  /** Šema boja. ⚠ SYNC: podrazumevana vrednost = ThemeService default (delivery ne zadaje
   *  DEFAULT_APP_THEME → plum) = pre-boot skripta u index.html. */
  theme: AppTheme = 'plum';
}

/** Vrednost svakog ključa kao JSON — za poređenje dva stanja podešavanja (koji ključevi su promenjeni). */
function settingsSnapshot(value: Settings): Record<string, string> {
  const snapshot: Record<string, string> = {};
  for (const key of Object.keys(value)) {
    snapshot[key] = JSON.stringify((value as any)[key]);
  }
  return snapshot;
}

/**
 * Podešavanja uređaja = defaults (Settings klasa) → lokalno sačuvane vrednosti.
 * Isti model kao u ostalim app-ovima (KD, konobar), ali bez profila uređaja: delivery
 * profil prosleđuje samo native sekciju shell-u (v. AppComponent).
 */
@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  private _settings?: BehaviorSubject<Settings>;

  constructor(private themeService: ThemeService,
    private logger: NGXLogger) {
  }

  public get settings(): BehaviorSubject<Settings> {
    if (!this._settings) {
      this._settings = new BehaviorSubject<Settings>(this.computeEffective());
      this.logSettings(this._settings);
    }
    return this._settings;
  }

  save(value: Settings) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      // localStorage nedostupan/pun — podešavanje važi samo do sledećeg pokretanja
    }

    this._settings?.next(value);
  }

  private computeEffective(): Settings {
    const set = new Settings();
    try {
      // Samo poznati ključevi: u "settings" na istom origin-u mogu ostati ključevi drugih/starijih
      // app-ova — njih ne preuzimamo.
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      for (const key of Object.keys(set)) {
        if (stored && typeof stored === "object" && key in stored) {
          (set as any)[key] = stored[key];
        }
      }
    } catch {
      // neispravan JSON / localStorage nedostupan — ostaju podrazumevana podešavanja
    }

    this.migrateSettings(set);

    return set;
  }

  private migrateSettings(value: Settings) {
    // Tema je ranije bila zaseban ključ core ThemeService-a ('appTheme'), van podešavanja
    // uređaja — zatečeni izbor prelazi u settings; takeLegacyTheme() pri tom briše stari ključ.
    const legacyTheme = this.themeService.takeLegacyTheme();
    if (legacyTheme) {
      value.theme = legacyTheme;
      this.save(value);
    }
  }

  /**
   * Kompletna podešavanja u log — odmah pri učitavanju, pa posle svake promene, da se iz loga
   * uređaja vidi sa kojim podešavanjima je app radio. Promene idu kroz debounce (kao na KD-u),
   * da niz brzih snimanja ne upiše ceo settings za svaku izmenu.
   */
  private logSettings(settings: BehaviorSubject<Settings>) {
    let logged = settingsSnapshot(settings.value);
    this.logger.info("Settings loaded", { settings: settings.value });

    settings.pipe(skip(1), debounceTime(1500)).subscribe(value => {
      const snapshot = settingsSnapshot(value);
      const changed = Object.keys({ ...logged, ...snapshot }).filter(key => snapshot[key] !== logged[key]);

      // ponovno snimanje istih vrednosti nije promena
      if (!changed.length) {
        return;
      }

      logged = snapshot;
      this.logger.info("Settings changed: " + changed.join(", "), { settings: value });
    });
  }
}
